import {
  PROCESS_PANEL_TABS as PROCESS_PANEL,
  DOE_PANEL_TABS as DOE_PANEL,
  PROCESS_DETAILS_TABS as PROCESS_DETAILS,
  PROCESS_SIGNING_FILE_TABS as SIGN_PANEL,
  DASHBOARD_VIEW_TYPES as DASHBOARD_VIEWS
} from "@/utils/enumerate";
import {
  DOE_PANEL_TABS_TYPE,
  IBreadcrumbs,
  ITab,
  PROCESS_DETAILS_TABS_TYPE,
  PROCESS_PANEL_TABS_TYPE,
  SIGN_PANEL_TABS_TYPE
} from "@/store/types/router-types";
import { IObjectString } from "@/store/types/types";

const state = {
  breadcrumbs: [{ name: "Início", path: "/app" }] as IBreadcrumbs[],
  currentFilterProcessPanel: {} as IObjectString,
  currentTabProcessPanel: PROCESS_PANEL.SECTOR as ITab,
  currentTabDOEPanel: DOE_PANEL.AUTHORIZE as ITab,
  currentTabProcessDetails: PROCESS_DETAILS.BASIC_DATA as ITab,
  currentTabSignPanel: SIGN_PANEL.SIGNING_REQUIRED as ITab,
  viewSectorInbox: DASHBOARD_VIEWS.SECTOR as string,
  viewSectorProcessed: DASHBOARD_VIEWS.INDIVIDUAL as string,
  viewSectorArchive: DASHBOARD_VIEWS.INDIVIDUAL as string
};

type State = typeof state;

const mutations = {
  setBreadcrumbs: (state: State, newRoute: IBreadcrumbs) => {
    const checkRoute = state.breadcrumbs.find(
      route => route.name === newRoute.name
    );

    const index = checkRoute ? state.breadcrumbs.indexOf(checkRoute) : -1;

    if (index > -1) state.breadcrumbs.splice(index);

    if (
      [
        "Novo Processo",
        "Painel",
        "Assinaturas",
        "Processos",
        "Documentos DOE",
        "Administração",
        "Detalhamento",
        "Configurações do Usuário"
      ].includes(newRoute.name)
    ) {
      state.breadcrumbs = [];

      if (newRoute.name === "Detalhamento") {
        state.breadcrumbs.push({
          name: "Processos",
          path: "/app/processos"
        });
      }
    }

    if (
      [
        "Assuntos",
        "Endereços e logos",
        "Marcadores",
        "Modelos de documentos",
        "Usuários",
        "Gestão de unidades",
        "Modelos oficiais",
        "Migrações",
        "Permissões",
        "Perfis",
        "Mensagens",
        "Relatórios"
      ].includes(newRoute.name)
    ) {
      state.breadcrumbs = [];

      state.breadcrumbs.push({
        name: "Administração",
        path: "/app/admin"
      });
    }

    state.breadcrumbs.push(newRoute);

    if (["Processos", "Detalhamento"].includes(newRoute.name))
      state.breadcrumbs.push({
        name:
          newRoute.name === "Detalhamento"
            ? state.currentTabProcessDetails.name
            : state.currentTabProcessPanel.name,
        path: null
      });
    else if (
      state.breadcrumbs.find(
        crumbs =>
          crumbs.name === state.currentTabProcessDetails.name ||
          crumbs.name === state.currentTabProcessPanel.name
      )
    ) {
      state.breadcrumbs.splice(state.breadcrumbs.length - 2, 1);
    }

    if (state.breadcrumbs.length > 5) {
      state.breadcrumbs.splice(1, 1);
    }
  },

  setCurrentTabProcessPanel: (
    state: State,
    tabName: PROCESS_PANEL_TABS_TYPE
  ) => {
    switch (tabName) {
      case PROCESS_PANEL.IN_ANALYSIS.value:
      case PROCESS_PANEL.DRAFTS.value:
      case PROCESS_PANEL.FAVORITES.value:
      case PROCESS_PANEL.SECTOR.value:
      case PROCESS_PANEL.PROCESSED.value:
      case PROCESS_PANEL.ARCHIVED.value:
        state.currentTabProcessPanel = PROCESS_PANEL[tabName];
        break;
      default:
        state.currentTabProcessPanel = PROCESS_PANEL.IN_ANALYSIS;
    }
    state.breadcrumbs.pop();
    state.breadcrumbs.push({
      name: state.currentTabProcessPanel.name,
      path: null
    });
  },

  setCurrentFilterProcessPanel: (state: State, value: IObjectString) => {
    state.currentFilterProcessPanel = value;
  },

  setCurrentTabDOEPanel: (state: State, tabName: DOE_PANEL_TABS_TYPE) => {
    switch (tabName) {
      case DOE_PANEL.AUTHORIZE.value:
      case DOE_PANEL.PROGRAMMED.value:
      case DOE_PANEL.REFUSED.value:
      case DOE_PANEL.PUBLICATED.value:
        state.currentTabDOEPanel = DOE_PANEL[tabName];
        break;
      default:
        state.currentTabDOEPanel = DOE_PANEL.AUTHORIZE;
    }
    if (state.breadcrumbs.length > 2) state.breadcrumbs.pop();
    state.breadcrumbs.push({
      name: state.currentTabDOEPanel.name,
      path: null
    });
  },

  setCurrentTabProcessDetails: (
    state: State,
    tabName: PROCESS_DETAILS_TABS_TYPE
  ) => {
    switch (tabName) {
      case PROCESS_DETAILS.BASIC_DATA.value:
      case PROCESS_DETAILS.ORDERS.value:
      case PROCESS_DETAILS.DOCUMENTS.value:
      case PROCESS_DETAILS.TIMELINE.value:
      case PROCESS_DETAILS.MERGE_PROCESS.value:
      case PROCESS_DETAILS.MERGE_DOCUMENTS.value:
      case PROCESS_DETAILS.PENDING_DOCUMENTS.value:
        state.currentTabProcessDetails = PROCESS_DETAILS[tabName];
        break;
      default:
        state.currentTabProcessDetails = PROCESS_DETAILS.BASIC_DATA;
    }
    state.breadcrumbs.pop();
    state.breadcrumbs.push({
      name: state.currentTabProcessDetails.name,
      path: null
    });
  },

  setCurrentTabSignaturePanel: (
    state: State,
    tabName: SIGN_PANEL_TABS_TYPE
  ) => {
    switch (tabName) {
      case SIGN_PANEL.SIGNING_REQUIRED.value:
      case SIGN_PANEL.SIGNING_REQUESTED.value:
      case SIGN_PANEL.SIGNING_SIGNED.value:
        state.currentTabSignPanel = SIGN_PANEL[tabName];
        break;
      default:
        state.currentTabSignPanel = SIGN_PANEL.SIGNING_REQUIRED;
    }
  },

  setViewSectorInbox: (state: State, viewSectorType: string) => {
    state.viewSectorInbox = viewSectorType;
  },

  setViewSectorProcessed: (state: State, viewSectorType: string) => {
    state.viewSectorProcessed = viewSectorType;
  },

  setViewSectorArchive: (state: State, viewSectorType: string) => {
    state.viewSectorArchive = viewSectorType;
  },

  setViewProcessesToStandard: (state: State) => {
    state.viewSectorInbox = DASHBOARD_VIEWS.SECTOR;
    state.viewSectorProcessed = DASHBOARD_VIEWS.INDIVIDUAL;
    state.viewSectorArchive = DASHBOARD_VIEWS.INDIVIDUAL;
  }
};

interface Getters {
  getViewSector: () => string;
}

const getters = {
  getBreadcrumbs: (state: State) => state.breadcrumbs,
  getCurrentTabProcessPanel: (state: State) => state.currentTabProcessPanel,
  getCurrentTabDOEPanel: (state: State) => state.currentTabDOEPanel,
  getCurrentTabProcessDetails: (state: State) => state.currentTabProcessDetails,
  getCurrentFilterProcessPanel: (state: State) =>
    state.currentFilterProcessPanel,
  getCurrentTabSignPanel: (state: State) => state.currentTabSignPanel,
  getViewSectorInbox: (state: State) => state.viewSectorInbox,
  getViewSectorProcessed: (state: State) => state.viewSectorProcessed,
  getViewSectorArchive: (state: State) => state.viewSectorArchive,
  getDefaultViewSector: (state: State, getters: Getters) => {
    const actualTab = state.currentTabProcessPanel.value as
      | "SECTOR"
      | "PROCESSED"
      | "ARCHIVED";
    const sectors = {
      SECTOR: state.viewSectorInbox,
      PROCESSED: state.viewSectorProcessed,
      ARCHIVED: state.viewSectorArchive
    };
    return sectors[actualTab] || getters.getViewSector;
  }
};

export default {
  state,
  mutations,
  getters
};
